import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DeviceTile } from "./DeviceTile";
import { DeviceListItem } from "./DeviceListItem";
import { DeviceFilters, DeviceFilterState } from "./DeviceFilters";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DeviceDetails } from "./DeviceDetails";
import { useState, useMemo } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { RefreshCw, LayoutGrid, Layers, Building, List } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { filterAndSortDevices } from "@/utils/deviceFilters";
import { compareOrganisationNames } from "@/utils/organisationSort";
import { GridLayoutSelector } from "./GridLayoutSelector";
import { useVisibilityRefresh } from "@/hooks/useVisibilityRefresh";
import { useCentralizedDeviceRealtime } from "@/hooks/use-centralized-device-realtime";
import { GroupedDeviceTile } from "./GroupedDeviceTile";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCommercialStatus } from "@/hooks/use-commercial-status";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export interface DeviceAssignment {
  id: string;
  device_id: string;
  user_id: string;
  organization_id?: string | null;
  organization_name?: string | null;
  device_details?: {
    device_last_updated: string | null;
    device_name?: string | null;
    device_description?: string | null;
    group_name?: string | null;
    group_color?: string | null;
    notification?: {
      result?: string;
      message?: string;
    } | null;
    location?: {
      name?: string;
    } | null;
    lat_wgs84?: number | null;
    long_wgs84?: number | null;
    device_status?: string | null;
    imagekit?: {
      thumbnailUrl?: string;
    } | null;
  };
}

interface GroupedDevice {
  groupName: string;
  groupColor?: string | null;
  deviceCount: number;
  mostRecentUpdate: string | null;
  thumbnailUrl: string | null;
}

interface OrganizationGroup {
  organizationId: string | null;
  organizationName: string;
  deviceCount: number;
  devices: DeviceAssignment[];
}

interface DeviceGridProps {
  gridColumns?: number;
  onGridColumnsChange?: (columns: number) => void;
}

type ViewMode = 'devices' | 'groups' | 'organizations' | 'list';
type CommercialFilter = 'all' | 'pilot' | 'free' | 'commercial';

export const DeviceGrid = ({ gridColumns = 3, onGridColumnsChange }: DeviceGridProps) => {
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('devices');
  const [commercialFilter, setCommercialFilter] = useState<CommercialFilter>('all');
  const [filters, setFilters] = useState<DeviceFilterState>({
    search: "",
    sortBy: "recent",
    sortDirection: "desc",
    showAlertsOnly: false,
    showOfflineOnly: false,
    showDemoOnly: false,
    groupName: "",
  });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { getFlagsForDevice } = useCommercialStatus();
  
  // Enable automatic refresh on tab visibility changes
  useVisibilityRefresh();
  
  // Set up centralized realtime subscription for ALL devices
  useCentralizedDeviceRealtime();

  const { data: assignments, isLoading, error, refetch } = useQuery({
    queryKey: ["device-assignments"],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session) {
        navigate("/");
        throw new Error("No session");
      }

      // Check user role to determine device access
      const { data: userRole } = await supabase.rpc('get_user_role', {
        user_id: session.session.user.id
      });

      // For superusers, get all devices; for others, get assigned devices
      if (userRole === 'superuser') {
        const PRODUCTION_ORG_ID = "7a4a3ed7-f614-4e4a-9eeb-dba745740c35";
        // Get all devices for superusers with organization info
        const { data: allDevices, error } = await supabase
          .from("device_details")
          .select(`
            device_id,
            device_name,
            device_serial,
            device_description,
            device_last_updated,
            device_status,
            group_name,
            group_id,
            notification,
            location,
            lat_wgs84,
            long_wgs84,
            imagekit,
            organization_id,
            organizations!device_details_organization_id_fkey(id, name),
            device_groups!device_details_group_id_fkey(color)
          `)
          .or(`organization_id.is.null,organization_id.neq.${PRODUCTION_ORG_ID}`)
          .order('device_last_updated', { ascending: false, nullsFirst: false });
        
        if (error) {
          console.error("Error fetching all devices for superuser:", error);
          throw error;
        }

        // Transform to match DeviceAssignment interface for superusers
        const superuserAssignments: DeviceAssignment[] = allDevices?.map(device => {
          // Org ownership now lives directly on device_details (populated by webhook)
          const orgRow = (device as any).organizations;
          const orgId = (device as any).organization_id || null;
          const orgName = orgRow?.name || null;

          return {
            id: `superuser-${device.device_id}`,
            device_id: device.device_id,
            user_id: session.session.user.id,
            organization_id: orgId,
            organization_name: orgName,
            device_details: {
              device_last_updated: device.device_last_updated,
              device_name: device.device_name,
              device_description: device.device_description,
              device_serial: (device as any).device_serial,
              group_name: (device as any).group_name,
              group_color: (device as any).device_groups?.color || null,
              notification: device.notification as any,
              location: device.location as any,
              lat_wgs84: device.lat_wgs84,
              long_wgs84: device.long_wgs84,
              device_status: (device as any).device_status,
              imagekit: device.imagekit as any,
            }
          };
        }) || [];

        console.log(`Superuser accessed all devices (${superuserAssignments.length} total)`);
        return { assignments: superuserAssignments, isSuperuser: true };
      } else {
        // Standard user behavior - get only assigned devices
        const { data, error } = await supabase
          .from("device_assignments")
          .select(`
            *,
            device_details:device_details!device_assignments_device_id_fkey(
              device_last_updated,
              device_name,
              device_serial,
              device_description,
              device_status,
              group_name,
              group_id,
              notification,
              location,
              lat_wgs84,
              long_wgs84,
              imagekit,
              device_groups!device_details_group_id_fkey(color)
            )
          `)
          .eq('user_id', session.session.user.id);
        
        if (error) {
          console.error("Error fetching assignments:", error);
          throw error;
        }

        // Sort assignments by last_updated timestamp, newest first
        const sortedAssignments = (data as DeviceAssignment[]).sort((a, b) => {
          const dateA = a.device_details?.device_last_updated ? new Date(a.device_details.device_last_updated) : new Date(0);
          const dateB = b.device_details?.device_last_updated ? new Date(b.device_details.device_last_updated) : new Date(0);
          return dateB.getTime() - dateA.getTime();
        });
        return { assignments: sortedAssignments, isSuperuser: false };
      }
    },
    staleTime: 60 * 1000, // 60 seconds - allow data to become stale
    refetchOnWindowFocus: true, // Enable refetch when tab becomes active
    retry: 1,
  });

  const deviceAssignments = assignments?.assignments || [];
  const isSuperuser = assignments?.isSuperuser || false;

  // Apply filters to the assignments - moved here for useMemo dependency
  const filteredAssignments = useMemo(() => {
    const base = deviceAssignments ? filterAndSortDevices(deviceAssignments, filters) : [];
    if (commercialFilter === 'all') return base;
    return base.filter((a) => {
      const flags = getFlagsForDevice(a.device_id, a.organization_id);
      if (commercialFilter === 'pilot') return flags.isPilot;
      if (commercialFilter === 'free') return flags.isFreeCamera;
      return !flags.isPilot && !flags.isFreeCamera;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceAssignments, filters, commercialFilter, getFlagsForDevice]);

  // Group devices by group_name for grouped view - must be before early returns
  const groupedDevices = useMemo<GroupedDevice[]>(() => {
    if (!filteredAssignments.length) return [];

    const groupMap = new Map<string, DeviceAssignment[]>();

    filteredAssignments.forEach((assignment) => {
      const rawGroupName = assignment.device_details?.group_name;
      const groupName = (!rawGroupName || rawGroupName.toLowerCase() === 'none') 
        ? 'Ungrouped' 
        : rawGroupName;
      if (!groupMap.has(groupName)) {
        groupMap.set(groupName, []);
      }
      groupMap.get(groupName)!.push(assignment);
    });

    return Array.from(groupMap.entries()).map(([groupName, devices]) => {
      // Sort devices by last updated to get most recent
      const sortedDevices = [...devices].sort((a, b) => {
        const dateA = a.device_details?.device_last_updated ? new Date(a.device_details.device_last_updated) : new Date(0);
        const dateB = b.device_details?.device_last_updated ? new Date(b.device_details.device_last_updated) : new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      const mostRecentDevice = sortedDevices[0];
      
      return {
        groupName,
        groupColor: mostRecentDevice?.device_details?.group_color || null,
        deviceCount: devices.length,
        mostRecentUpdate: mostRecentDevice?.device_details?.device_last_updated || null,
        thumbnailUrl: mostRecentDevice?.device_details?.imagekit?.thumbnailUrl || null,
      };
    }).sort((a, b) => {
      // Sort groups by most recent update
      const dateA = a.mostRecentUpdate ? new Date(a.mostRecentUpdate) : new Date(0);
      const dateB = b.mostRecentUpdate ? new Date(b.mostRecentUpdate) : new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
  }, [filteredAssignments]);

  // Organisation headings are A–Z; camera ordering within each is unchanged.
  const organizationGroups = useMemo<OrganizationGroup[]>(() => {
    if (!isSuperuser || !filteredAssignments.length) return [];

    const orgMap = new Map<string | null, DeviceAssignment[]>();
    
    filteredAssignments.forEach((assignment) => {
      const orgId = assignment.organization_id || null;
      if (!orgMap.has(orgId)) {
        orgMap.set(orgId, []);
      }
      orgMap.get(orgId)!.push(assignment);
    });

    return Array.from(orgMap.entries()).map(([orgId, devices]) => {
      // Get organization name from first device
      const orgName = devices[0]?.organization_name || 'Unassigned';
      
      return {
        organizationId: orgId,
        organizationName: orgName,
        deviceCount: devices.length,
        devices: devices.sort((a, b) => {
          if (filters.sortBy === 'name') {
            const nameA = a.device_details?.device_name || "";
            const nameB = b.device_details?.device_name || "";
            return nameA.localeCompare(nameB);
          }
          // Default to date sorting (most recent first)
          const dateA = a.device_details?.device_last_updated ? new Date(a.device_details.device_last_updated) : new Date(0);
          const dateB = b.device_details?.device_last_updated ? new Date(b.device_details.device_last_updated) : new Date(0);
          return dateB.getTime() - dateA.getTime();
        }),
      };
    }).sort((a, b) => {
      if (!a.organizationId) return b.organizationId ? 1 : 0;
      if (!b.organizationId) return -1;
      return compareOrganisationNames(a.organizationName, b.organizationName);
    });
  }, [filteredAssignments, isSuperuser, filters]);

  // Extract unique group names from all devices for filter dropdown
  const availableGroups = deviceAssignments
    ? Array.from(
        new Set(
          deviceAssignments
            .map((a) => a.device_details?.group_name)
            .filter((name): name is string => !!name && name.toLowerCase() !== "none")
        )
      ).sort()
    : [];

  // Removed excessive invalidation effect - now relying on centralized realtime subscription

  const handleRefresh = async () => {
    if (!deviceAssignments.length) return;
    
    toast.success("Refreshing device data from API...");
    
    try {
      // Invalidate device assignments to get fresh data
      await queryClient.invalidateQueries({ 
        queryKey: ["device-assignments"],
        refetchType: 'all'
      });
      
      // Force invalidate all device details with staleTime: 0 to bypass cache
      const refreshPromises = deviceAssignments.map(async (assignment) => {
        await queryClient.invalidateQueries({
          queryKey: ["device-details", assignment.device_id],
          refetchType: 'all'
        });
        await queryClient.invalidateQueries({
          queryKey: ["device", assignment.device_id], 
          refetchType: 'all'
        });
      });
      
      await Promise.all(refreshPromises);
      toast.success("All devices refreshed from API");
    } catch (error) {
      console.error("Error during bulk refresh:", error);
      toast.error("Some devices failed to refresh");
    }
  };

  const handleDeviceClick = (deviceId: string) => {
    setSelectedDevice(deviceId);
    if (!isMobile) {
      setIsDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedDevice(null);
  };

  const handleGroupClick = (groupName: string) => {
    // Filter to show devices in this group and switch to devices view
    setFilters(prev => ({
      ...prev,
      groupName: groupName === 'Ungrouped' ? '' : groupName,
    }));
    setViewMode('devices');
  };

  if (error) {
    return (
      <div className="text-sm text-red-500">
        Error loading devices. Please try refreshing the page.
      </div>
    );
  }

  if (isLoading) {
    return <div className="text-sm">Loading devices...</div>;
  }

  return (
    <div className="device-grid w-full max-w-full overflow-x-hidden">
      <div className="brand-device-toolbar flex flex-col gap-3 lg:flex-row lg:justify-between lg:items-center mb-4 w-full">
        <h2 className="text-lg font-bold">
          {isSuperuser ? 'All Devices' : 'Your Devices'}
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          {/* View Mode Toggle */}
          {isSuperuser && (
            <Select value={commercialFilter} onValueChange={(v) => setCommercialFilter(v as CommercialFilter)}>
              <SelectTrigger className="h-8 w-[150px] text-xs">
                <SelectValue placeholder="All devices" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All devices</SelectItem>
                <SelectItem value="pilot">Pilot Pack</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
              </SelectContent>
            </Select>
          )}
          <ToggleGroup type="single" variant="outline" value={viewMode} onValueChange={(v) => v && setViewMode(v as ViewMode)}>
            <Tooltip>
              <TooltipTrigger asChild>
                <ToggleGroupItem value="devices" aria-label="Grid view" className="h-8 w-8 p-0 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-transparent">
                  <LayoutGrid className="h-4 w-4" />
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent>Grid View</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <ToggleGroupItem value="list" aria-label="List view" className="h-8 w-8 p-0 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-transparent">
                  <List className="h-4 w-4" />
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent>List View</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <ToggleGroupItem value="groups" aria-label="Group view" className="h-8 w-8 p-0 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-transparent">
                  <Layers className="h-4 w-4" />
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent>View by Group</TooltipContent>
            </Tooltip>
            {isSuperuser && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem value="organizations" aria-label="Organization view" className="h-8 w-8 p-0 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-transparent">
                    <Building className="h-4 w-4" />
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent>View by Organization</TooltipContent>
              </Tooltip>
            )}
          </ToggleGroup>
          {onGridColumnsChange && (
            <GridLayoutSelector value={gridColumns} onValueChange={onGridColumnsChange} />
          )}
          <Button
            onClick={handleRefresh}
            variant="outline"
            size="sm"
            className="gap-2 text-xs"
          >
            <RefreshCw className="h-3 w-3" />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Device Filters - shown in all views */}
      <DeviceFilters
        filters={filters}
        onFiltersChange={setFilters}
        totalDevices={deviceAssignments.length}
        filteredCount={viewMode === 'groups' ? groupedDevices.length : filteredAssignments.length}
        availableGroups={viewMode === 'groups' ? [] : availableGroups}
      />
      
      {viewMode === 'organizations' && isSuperuser ? (
        // Organizations View (Superuser Only)
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground mb-4">
            {organizationGroups.length} organizations • {filteredAssignments.length} devices
          </p>
          <Accordion type="multiple" className="w-full">
            {organizationGroups.map((org) => (
              <AccordionItem key={org.organizationId || 'unassigned'} value={org.organizationId || 'unassigned'}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{org.organizationName}</span>
                    <span className="text-sm text-muted-foreground">
                      ({org.deviceCount} device{org.deviceCount !== 1 ? 's' : ''})
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-${gridColumns} gap-4 pt-2`}>
                    {org.devices.map((assignment) => (
                      isMobile ? (
                        <Sheet key={assignment.id}>
                          <SheetTrigger asChild>
                            <div onClick={() => handleDeviceClick(assignment.device_id)}>
                              <DeviceTile deviceId={assignment.device_id} organizationId={assignment.organization_id} />
                            </div>
                          </SheetTrigger>
                          <SheetContent side="right" className="p-3 w-full sm:max-w-[85vw] overflow-hidden">
                            <ScrollArea className="h-full w-full max-w-full overflow-x-hidden">
                              {selectedDevice === assignment.device_id && (
                                <DeviceDetails deviceId={assignment.device_id} />
                              )}
                            </ScrollArea>
                          </SheetContent>
                        </Sheet>
                      ) : (
                        <div key={assignment.id} onClick={() => handleDeviceClick(assignment.device_id)}>
                          <DeviceTile deviceId={assignment.device_id} organizationId={assignment.organization_id} />
                        </div>
                      )
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      ) : viewMode === 'groups' ? (
        // Grouped View
        <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-${gridColumns} gap-6 w-full`}>
          {groupedDevices.map((group) => (
            <GroupedDeviceTile
              key={group.groupName}
              groupName={group.groupName}
              groupColor={group.groupColor}
              deviceCount={group.deviceCount}
              thumbnailUrl={group.thumbnailUrl}
              mostRecentUpdate={group.mostRecentUpdate}
              onClick={() => handleGroupClick(group.groupName)}
            />
          ))}
        </div>
      ) : viewMode === 'list' ? (
        // List View
        <div className="flex flex-col gap-2 w-full">
          {filteredAssignments.map((assignment) => (
            <DeviceListItem
              key={assignment.id}
              deviceId={assignment.device_id}
              organizationId={assignment.organization_id}
              onClick={() => handleDeviceClick(assignment.device_id)}
            />
          ))}
        </div>
      ) : (
        // Devices View (cards)
        <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-${gridColumns} gap-6 w-full`}>
          {filteredAssignments.map((assignment) => (
            isMobile ? (
              // Mobile view - use Sheet with padding
              <Sheet key={assignment.id}>
                <SheetTrigger asChild>
                  <div onClick={() => handleDeviceClick(assignment.device_id)}>
                    <DeviceTile deviceId={assignment.device_id} organizationId={assignment.organization_id} />
                  </div>
                </SheetTrigger>
                <SheetContent side="right" className="p-3 w-full sm:max-w-[85vw] overflow-hidden">
                  <ScrollArea className="h-full w-full max-w-full overflow-x-hidden">
                    {selectedDevice === assignment.device_id && (
                      <DeviceDetails deviceId={assignment.device_id} />
                    )}
                  </ScrollArea>
                </SheetContent>
              </Sheet>
            ) : (
              // Desktop view - use Dialog
              <div key={assignment.id} onClick={() => handleDeviceClick(assignment.device_id)}>
                <DeviceTile deviceId={assignment.device_id} organizationId={assignment.organization_id} />
              </div>
            )
          ))}
        </div>
      )}

      {/* Dialog for desktop view */}
      {!isMobile && (
        <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
          <DialogContent className="w-[90vw] h-[80vh] max-w-[80vw] max-h-[80vh] p-0 overflow-hidden">
            <ScrollArea className="h-full w-full max-w-full overflow-x-hidden">
              {selectedDevice && (
                <DeviceDetails deviceId={selectedDevice} />
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
